'use client';

import { useEffect, useState } from 'react';
import {
  canProcessAvifInBrowser,
  createAvifPreviewBlobFromFile,
  readAvifSource,
  type DecodedAvifSource,
} from '@/lib/browser-avif-tools';

export type LoadedAvifSource = DecodedAvifSource & {
  previewUrl: string;
};

export function useLoadedAvifSource() {
  const [source, setSource] = useState<LoadedAvifSource | null>(null);

  useEffect(() => {
    return () => {
      if (source?.previewUrl) {
        URL.revokeObjectURL(source.previewUrl);
      }
    };
  }, [source]);

  async function loadSource(file: File) {
    if (!canProcessAvifInBrowser()) {
      throw new Error(
        'This browser cannot process AVIF locally yet. Try a modern Chromium or Safari build with ImageDecoder and OffscreenCanvas support.',
      );
    }

    const [decoded, previewBlob] = await Promise.all([
      readAvifSource(file),
      createAvifPreviewBlobFromFile(file),
    ]);

    setSource((current) => {
      if (current?.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }

      return {
        ...decoded,
        previewUrl: URL.createObjectURL(previewBlob),
      };
    });
  }

  function clearSource() {
    setSource((current) => {
      if (current?.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }

      return null;
    });
  }

  return {
    source,
    loadSource,
    clearSource,
    isSupported: canProcessAvifInBrowser(),
  };
}
